import { CalendarDays, Clock, Crown, MapPin, Sparkles, Star, Users, Zap } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { categoryToAccentVar } from '../../util/categoryAccent';
import { billingIntervalSuffix } from '../../util/subscriptionBilling';
import css from './SubscriptionCard.module.css';

const TIER_ICONS = {
  0: Zap,
  1: Crown,
  2: Sparkles,
};

export const formatTierPrice = (
  priceAmount = 0,
  billingInterval = 'month',
  currency = 'usd',
  billingType = 'recurring',
  customIntervalUnit = null,
  customIntervalCount = null
) => {
  const amount = Number(priceAmount) / 100;
  const currencyPrefix = currency?.toLowerCase() === 'usd' ? '$' : `${currency?.toUpperCase()} `;
  const formattedAmount = Number.isFinite(amount) ? amount.toFixed(amount % 1 === 0 ? 0 : 2) : 0;
  if (billingType === 'one_time') {
    return `${currencyPrefix}${formattedAmount}`;
  }
  const suffix = billingIntervalSuffix({ billingInterval, customIntervalUnit, customIntervalCount });
  return `${currencyPrefix}${formattedAmount}${suffix}`;
};

// Coach-level subscription card: shared between the landing page's "Featured
// Subscriptions" rail and the /s search results grid. `compact` trims the tier
// list to fit a narrower grid slot instead of the rail's wider card.
const SubscriptionCard = ({ subscription, compact = false }) => {
  const history = useHistory();
  const maxTiers = compact ? 2 : 3;

  // Go straight to the coach's own profile page and scroll to their
  // subscriptions section -- the old /listing/:slug/:listingId route this
  // used to point at depends on a live Sharetribe listing fetch, which
  // 500s for every pre-migration coach now that those credentials are
  // revoked (confirmed via a clean network trace against a real coach's
  // card, not assumed).
  const goToCoachProfile = () => {
    if (!subscription.coachId) return;
    history.push(`/u/${subscription.coachId}?section=subscriptions`);
  };

  const accentVar = categoryToAccentVar(subscription.categories?.[0]);

  return (
    <article className={css.card}>
      <div className={css.topAccent} style={{ background: accentVar }} />
      <div className={css.cardHead}>
        {subscription.coachImageUrl ? (
          <img
            className={css.avatar}
            src={subscription.coachImageUrl}
            alt={subscription.coachName || 'Coach'}
            loading="lazy"
            onClick={goToCoachProfile}
            style={{ cursor: subscription.coachId ? 'pointer' : undefined }}
          />
        ) : (
          <div
            className={css.avatarPlaceholder}
            onClick={goToCoachProfile}
            style={{ cursor: subscription.coachId ? 'pointer' : undefined }}
          >
            {(subscription.coachName || 'Coach').charAt(0)}
          </div>
        )}
        <div className={css.coachInfo}>
          <div className={css.coachNameRow}>
            <h3 className={css.coachName}>{subscription.coachName || 'Coach'}</h3>
            {subscription.isPro ? <span className={css.proBadge}>PRO</span> : null}
          </div>
          <div className={css.metaRow}>
            <MapPin size={14} />
            <span>{subscription.coachLocation || 'Location not specified'}</span>
          </div>
          <div className={css.metaRowStrong}>
            <Star size={14} />
            <span>
              {subscription.coachRating || 0} ({subscription.coachReviewCount || 0})
            </span>
            <Users size={14} />
            <span>{subscription.subscriberCount || 0} subscribers</span>
          </div>
        </div>
      </div>

      {subscription.categories?.length ? (
        <div className={css.badges}>
          {subscription.categories.slice(0, 2).map((category, idx) => (
            <span key={`${category}-${idx}`} className={idx === 0 ? css.badge : css.badgeSecondary}>
              {category}
            </span>
          ))}
        </div>
      ) : null}

      {!compact && subscription.description ? (
        <p className={css.description}>{subscription.description}</p>
      ) : null}

      <div className={css.tierList}>
        {(subscription.tiers || []).slice(0, maxTiers).map((tier, index) => {
          const TierIcon = TIER_ICONS[index] || Zap;
          return (
            <div key={tier.id} className={tier.isPopular ? css.tierPopular : css.tier}>
              {tier.isPopular ? <span className={css.popularBadge}>Popular</span> : null}
              <div className={css.tierContent}>
                <div className={tier.isPopular ? css.tierIconPopular : css.tierIcon}>
                  <TierIcon size={12} />
                </div>
                <div>
                  <div className={css.tierName}>{tier.name}</div>
                  <div className={css.tierMeta}>
                    {tier.creditsPerCycle > 0 ? <Clock size={12} /> : <CalendarDays size={12} />}
                    <span>
                      {tier.creditsPerCycle > 0
                        ? tier.billingType === 'one_time'
                          ? `${tier.creditsPerCycle} session${tier.creditsPerCycle === 1 ? '' : 's'} included`
                          : `${tier.creditsPerCycle} sessions/mo`
                        : 'No session credits'}
                    </span>
                  </div>
                </div>
              </div>
              <div className={css.tierPriceWrap}>
                <span className={css.tierPrice}>
                  {formatTierPrice(tier.priceAmount, tier.billingInterval, tier.currency, tier.billingType)}
                </span>
              </div>
            </div>
          );
        })}
        {(subscription.tiers || []).length > maxTiers ? (
          <div className={css.moreTiers}>+{subscription.tiers.length - maxTiers} more</div>
        ) : null}
      </div>

      <button
        type="button"
        className={css.primaryButton}
        onClick={goToCoachProfile}
      >
        View Subscription Plans
      </button>
    </article>
  );
};

export default SubscriptionCard;
